import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { clearTaxOverride, confirmTaxOverride } from "@/lib/api";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const describeTaxOverride = (ov) => {
  if (!ov?.type) return "";
  return ov.type === "rate" ? `${ov.rate}%` : `$${(Number(ov.amount) || 0).toFixed(2)}`;
};

// "Tax manually set by [name] · [date]" — internal screens only, never on
// the customer PDF. `stale` = exact-dollar override whose base drifted.
export function TaxOverrideBadge({ override, stale, docType, docId, woId, canManage, onChange, onEdit, testId = "tax-override-badge" }) {
  const [busy, setBusy] = useState(null);
  if (!override?.type) return null;

  const run = async (kind) => {
    setBusy(kind);
    try {
      const res = kind === "clear" ? await clearTaxOverride(docType, docId, woId) : await confirmTaxOverride(docType, docId, woId);
      toast.success(kind === "clear" ? "Manual tax cleared — back to calculated" : "Manual tax re-confirmed");
      onChange?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the manual tax");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="text-[11px] leading-tight space-y-1" data-testid={testId}>
      <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900">
        <ShieldAlert className="w-3 h-3 text-amber-600" />
        <span data-testid={`${testId}-text`}>Tax manually set by {override.set_by_name || "—"} · {when(override.set_at)} · {describeTaxOverride(override)}{override.tax_no ? ` · ${override.tax_no}` : ""}</span>
      </div>
      {stale && (
        <div className="flex items-center gap-1.5 text-red-700" data-testid={`${testId}-stale`}>
          <AlertTriangle className="w-3 h-3" />
          <span>Line items changed since tax was set — review</span>
        </div>
      )}
      {canManage && (
        <div className="flex items-center gap-3 text-slate-500">
          {onEdit && <button type="button" className="underline hover:text-slate-800" onClick={onEdit} data-testid={`${testId}-change`}>Change</button>}
          {stale && (
            <button type="button" className="underline hover:text-slate-800 inline-flex items-center gap-1" onClick={() => run("confirm")} disabled={!!busy} data-testid={`${testId}-confirm`}>
              {busy === "confirm" && <Loader2 className="w-3 h-3 animate-spin" />} Re-confirm amount
            </button>
          )}
          <button type="button" className="underline hover:text-slate-800 inline-flex items-center gap-1" onClick={() => run("clear")} disabled={!!busy} data-testid={`${testId}-clear`}>
            {busy === "clear" && <Loader2 className="w-3 h-3 animate-spin" />} Clear
          </button>
        </div>
      )}
    </div>
  );
}
